<template>
    <div ref="rootRef" class="actions-menu" @keydown.esc="close">
        <button
            type="button"
            class="actions-menu__trigger"
            :aria-expanded="isOpen"
            aria-haspopup="menu"
            :disabled="disabled"
            @click="isOpen = !isOpen"
        >
            {{ label }} <span aria-hidden="true">▾</span>
        </button>
        <ul v-if="isOpen" class="actions-menu__list" role="menu">
            <li v-for="item in items" :key="item.id" role="none">
                <button
                    type="button"
                    role="menuitem"
                    :class="['actions-menu__item', { 'is-danger': item.danger }]"
                    :disabled="item.disabled"
                    :aria-disabled="item.disabled || undefined"
                    @click="select(item)"
                >
                    <span class="actions-menu__item-label">{{ item.label }}</span>
                    <span v-if="item.disabled && item.hint" class="actions-menu__item-hint">{{ item.hint }}</span>
                </button>
            </li>
        </ul>
    </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue';

/**
 * items: [{ id, label, danger?, disabled?, hint? }] — hint объясняет, почему пункт недоступен.
 */
defineProps({
    items: { type: Array, required: true },
    label: { type: String, default: 'Действия' },
    disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['select']);

const rootRef = ref(null);
const isOpen = ref(false);

const close = () => {
    isOpen.value = false;
};

const select = (item) => {
    if (item.disabled) return;
    close();
    emit('select', item.id);
};

const onDocumentPointerDown = (event) => {
    if (!rootRef.value?.contains(event.target)) close();
};

watch(isOpen, (open) => {
    if (open) document.addEventListener('pointerdown', onDocumentPointerDown);
    else document.removeEventListener('pointerdown', onDocumentPointerDown);
});

onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocumentPointerDown));
</script>

<style scoped lang="scss">
@use '../../../scss/pages/admin/admin-tokens' as *;

.actions-menu {
    position: relative;
}

.actions-menu__trigger {
    display: inline-flex;
    align-items: center;
    gap: $spacing-xs;
    padding: 0.35rem 0.7rem;
    border: 1px solid $admin-border-hover;
    border-radius: $radius-md;
    background: $admin-bg-primary;
    font-size: $font-size-sm;
    font-weight: 500;
    color: $admin-text-secondary;
    cursor: pointer;
    white-space: nowrap;

    &:hover:not(:disabled) { background: $admin-bg-hover; }
}

.actions-menu__list {
    position: absolute;
    right: 0;
    top: calc(100% + 4px);
    z-index: 20;
    min-width: 200px;
    margin: 0;
    padding: $spacing-xs 0;
    list-style: none;
    border: 1px solid $admin-border-color;
    border-radius: $radius-md;
    background: $admin-bg-primary;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
}

.actions-menu__item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    padding: $spacing-sm $spacing-lg;
    border: none;
    background: none;
    text-align: left;
    font-size: $font-size-base;
    color: $admin-text-primary;
    cursor: pointer;

    &:hover:not(:disabled) { background: $admin-bg-hover; }

    &.is-danger { color: $admin-danger-color; }

    &:disabled {
        cursor: not-allowed;
        color: $admin-text-light;
    }
}

.actions-menu__item-hint {
    font-size: $font-size-xs;
    color: $admin-text-muted;
    white-space: normal;
}
</style>
